import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  userControllerListSelfNewsEditions,
  userControllerMarkSelfNewsEditionSeen,
} from '@/generated/korza-api-client';
import type { NewsEditionsStatus } from '@/lib/whats-new/auto-open';
import type { CatalogEdition } from '@/lib/whats-new/edition-types';
import { resolveLatest } from '@/lib/whats-new/registry';

export const NEWS_EDITIONS_QUERY_KEY = ['newsEditions'] as const;

/**
 * Editions marked as seen in this tab (NEWS-18): they stay seen until the tab
 * closes even when the POST fails or a refetch still says `seenAt: null`.
 */
const seenInSession = new Set<string>();

export interface UseNewsEditions {
  /** `GET users/self/news-editions`, newest first (`[]` until it loads). */
  catalog: CatalogEdition[];
  /** First catalog edition with a module in the registry (NEWS-20). */
  latest: CatalogEdition | null;
  status: NewsEditionsStatus;
  /** Seen by this person (backend) or in this tab. */
  isSeen: (editionId: string) => boolean;
  seenInSession: ReadonlySet<string>;
  /**
   * Records the view (NEWS-17): marks it in the cache and in this tab right
   * away and POSTs without waiting; any failure is silent (NEWS-18/22).
   */
  markSeen: (editionId: string) => void;
}

export function useNewsEditions(): UseNewsEditions {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: NEWS_EDITIONS_QUERY_KEY,
    queryFn: async (): Promise<CatalogEdition[]> => {
      const response = await userControllerListSelfNewsEditions({
        throwOnError: true,
      });
      return response.data;
    },
  });

  const catalog = query.data ?? [];
  const status: NewsEditionsStatus = query.isError
    ? 'error'
    : query.isPending
      ? 'loading'
      : 'ready';

  const isSeen = useCallback(
    (editionId: string) =>
      seenInSession.has(editionId) ||
      (query.data ?? []).some(
        (edition) => edition.id === editionId && edition.seenAt !== null
      ),
    [query.data]
  );

  const markSeen = useCallback(
    (editionId: string) => {
      seenInSession.add(editionId);
      const now = new Date().toISOString();
      queryClient.setQueryData<CatalogEdition[]>(
        NEWS_EDITIONS_QUERY_KEY,
        (editions) =>
          editions?.map((edition) =>
            edition.id === editionId && edition.seenAt === null
              ? { ...edition, seenAt: now }
              : edition
          )
      );
      // Not awaited and its status is not acted on: 2xx, 400 (unknown id),
      // 5xx and network errors all leave the edition seen in this tab and
      // show no error (NEWS-17/18/22).
      void userControllerMarkSelfNewsEditionSeen({
        path: { editionId },
      }).catch(() => undefined);
    },
    [queryClient]
  );

  return {
    catalog,
    latest: resolveLatest(catalog),
    status,
    isSeen,
    seenInSession,
    markSeen,
  };
}
